import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Calendar, FileText, Target, Users, MessageSquare, Activity, Clock, ArrowLeft, AlertCircle } from 'lucide-react'
import { loadPracticeSession } from '../services/practiceSessionsApi'
import { TileType } from '../components/PracticePlanner/TilePalette'
import NavigationHeader from '../components/NavigationHeader'
import { useNavigate } from 'react-router-dom'

const getTileIcon = (type) => {
  switch (type) {
    case TileType.DRILL:
      return Target
    case TileType.SCRIMMAGE:
      return Users
    case TileType.SPEAKING_POINTS:
      return MessageSquare
    case TileType.FITNESS:
      return Activity
    default:
      return Target
  }
}

const getTileColorClasses = (type) => {
  switch (type) {
    case TileType.DRILL:
      return {
        border: 'border-green-300',
        icon: 'text-green-600',
        bg: 'bg-green-50'
      }
    case TileType.SCRIMMAGE:
      return {
        border: 'border-blue-300',
        icon: 'text-blue-600',
        bg: 'bg-blue-50'
      }
    case TileType.SPEAKING_POINTS:
      return {
        border: 'border-purple-300',
        icon: 'text-purple-600',
        bg: 'bg-purple-50'
      }
    case TileType.FITNESS:
      return {
        border: 'border-orange-300',
        icon: 'text-orange-600',
        bg: 'bg-orange-50'
      }
    default:
      return {
        border: 'border-gray-300',
        icon: 'text-gray-600',
        bg: 'bg-gray-50'
      }
  }
}

const PracticeTileView = ({ tile, index }) => {
  const Icon = getTileIcon(tile.type)
  const colorClasses = getTileColorClasses(tile.type)

  return (
    <div
      className={`bg-white border-2 ${colorClasses.border} rounded-lg p-4 md:p-6 shadow-sm ${colorClasses.bg}`}
    >
      <div className="flex items-start space-x-3 mb-3">
        <Icon size={24} className={`${colorClasses.icon} flex-shrink-0`} />
        <div className="flex-1 min-w-0">
          <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-1">
            {index + 1}. {tile.title || 'Untitled'}
          </h3>
          <div className="flex items-center space-x-2 text-sm text-gray-500">
            <Clock size={14} />
            <span>
              {tile.startTime || 0}-{tile.endTime || 0} minutes ({tile.duration || 10} min duration)
            </span>
          </div>
        </div>
      </div>

      {tile.description && (
        <div 
          className="text-sm md:text-base text-gray-700 mb-3 practice-tile-description"
          style={{ lineHeight: '1.6' }}
          dangerouslySetInnerHTML={{ __html: tile.description }}
        />
      )}

      {tile.playGifUrl && (
        <div className="mb-3">
          <a
            href={tile.playGifUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center space-x-2 text-sm md:text-base text-blue-600 hover:text-blue-800 hover:underline font-medium"
          >
            <span>View Play: {tile.playName || 'Linked Play'}</span>
          </a>
        </div>
      )}

      {(tile.links && tile.links.length > 0) || tile.link ? (
        <div className="space-y-2">
          {(tile.links || (tile.link ? [tile.link] : [])).map((link, linkIndex) => {
            const linkUrl = link.startsWith('http://') || link.startsWith('https://') 
              ? link 
              : `https://${link}`
            
            return (
              <a
                key={linkIndex}
                href={linkUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block p-3 bg-blue-50 border border-blue-200 rounded text-sm md:text-base text-blue-600 hover:text-blue-800 hover:bg-blue-100 transition-colors break-all"
              >
                {link}
              </a>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}

const PracticeViewPage = () => {
  const { sessionId } = useParams()
  const navigate = useNavigate()
  const [session, setSession] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const loadSession = async () => {
      if (!sessionId) {
        setError('No session ID provided')
        setIsLoading(false)
        return
      }

      try {
        setIsLoading(true)
        const loadedSession = await loadPracticeSession(sessionId)
        setSession(loadedSession)
      } catch (err) {
        console.error('Error loading session:', err)
        setError(err.message || 'Failed to load practice session')
      } finally {
        setIsLoading(false)
      }
    }

    loadSession()
  }, [sessionId])

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading practice session...</p>
        </div>
      </div>
    )
  }

  if (error || !session) {
    return (
      <div className="min-h-screen bg-gray-50">
        <NavigationHeader />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="bg-white rounded-lg border border-red-200 p-6 text-center">
            <AlertCircle size={48} className="text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Session Not Found</h2>
            <p className="text-gray-600 mb-4">
              {error || 'The practice session you are looking for does not exist or you do not have access to it.'}
            </p>
            <button
              onClick={() => navigate('/home')}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
            >
              Go to Home
            </button>
          </div>
        </div>
      </div>
    )
  }

  const sortedTiles = [...(session.tiles || [])].sort((a, b) => (a.startTime || 0) - (b.startTime || 0))
  const totalDuration = sortedTiles.length > 0 
    ? (sortedTiles[sortedTiles.length - 1].endTime || 0) + 1
    : 0

  const durationByType = sortedTiles.reduce((acc, tile) => {
    const type = tile.type || 'unknown'
    const duration = tile.duration || 10
    acc[type] = (acc[type] || 0) + duration
    return acc
  }, {})

  const typeLabels = {
    [TileType.DRILL]: 'Drills',
    [TileType.SCRIMMAGE]: 'Scrimmages',
    [TileType.SPEAKING_POINTS]: 'Speaking Points',
    [TileType.FITNESS]: 'Fitness'
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <NavigationHeader />
      
      <div className="max-w-4xl mx-auto px-4 py-6 md:py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate('/home')}
          className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <ArrowLeft size={20} />
          <span className="text-sm md:text-base">Back to Home</span>
        </button>

        {/* Session Header */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 md:p-6 mb-6">
          <div className="flex items-center space-x-3 mb-3">
            <Calendar size={24} className="text-gray-600 flex-shrink-0" />
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex-1">
              {session.name || 'Practice Session'}
            </h1>
          </div>
          
          {session.description && (
            <p className="text-sm md:text-base text-gray-600 mb-4 pl-9">
              {session.description}
            </p>
          )}

          {session.sportType && (
            <div className="pl-9">
              <span className="inline-block px-3 py-1 text-xs md:text-sm bg-gray-100 text-gray-700 rounded-full capitalize">
                {session.sportType}
              </span>
            </div>
          )}
        </div>

        {/* Practice Tiles */}
        {sortedTiles.length > 0 ? (
          <div className="space-y-4 md:space-y-6 mb-6">
            {sortedTiles.map((tile, index) => (
              <PracticeTileView key={tile.id} tile={tile} index={index} />
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 p-8 text-center mb-6">
            <p className="text-gray-500">No activities in this practice session.</p>
          </div>
        )}

        {/* Notes Section */}
        {session.notes && session.notes.trim() && (
          <div className="bg-white rounded-lg border border-gray-200 p-4 md:p-6 mb-6">
            <div className="flex items-center space-x-2 mb-3">
              <FileText size={20} className="text-gray-600" />
              <h3 className="text-lg md:text-xl font-semibold text-gray-900">Notes</h3>
            </div>
            <div 
              className="text-sm md:text-base text-gray-700 practice-tile-description"
              style={{ lineHeight: '1.6' }}
              dangerouslySetInnerHTML={{ __html: session.notes }}
            />
          </div>
        )}

        {/* Practice Summary */}
        {sortedTiles.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-4 md:p-6">
            <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-4">Practice Summary</h3>
            <div className="text-sm md:text-base text-gray-600 space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-gray-200">
                <span className="font-medium">Total Activities:</span>
                <span className="font-semibold text-gray-900">{sortedTiles.length}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-200">
                <span className="font-medium">Total Duration:</span>
                <span className="font-semibold text-gray-900">{totalDuration} minutes</span>
              </div>
              <div className="pt-2">
                <div className="font-medium mb-3">Duration by Type:</div>
                <div className="space-y-2">
                  {Object.entries(durationByType)
                    .sort(([a], [b]) => {
                      const order = [TileType.DRILL, TileType.SCRIMMAGE, TileType.SPEAKING_POINTS, TileType.FITNESS]
                      return order.indexOf(a) - order.indexOf(b)
                    })
                    .map(([type, duration]) => (
                      <div key={type} className="flex justify-between items-center py-1">
                        <span>{typeLabels[type] || type}:</span>
                        <span className="font-semibold">{duration} minutes</span>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default PracticeViewPage

